import { Stack, alpha } from '@mui/material';
import React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartTooltip } from '../../consts';
import { THEME_MODES } from '../../helpers/constant';
import { useThemeMode } from '../theme';
import { AppChartCirclePattern, AppChartWavePattern } from './chart-pattern';

function AppBarChart({ data }) {
    const { colours, themeMode, FONT } = useThemeMode();

    const bars = [
        { dataKey: "Negative", fill: colours.primary100, pattern: "chartWave", barColors: [colours.critical100, colours.critical100] },
        { dataKey: "Neutral", fill: colours.subduedgray, pattern: "", barColors: [colours.dividergray, colours.dividergray] },
        { dataKey: "Positive", fill: colours.green100, pattern: "chartCircle", barColors: [colours.success100, colours.success100] }
    ];

    // when no data in response.
    if (!data?.length) {
        return (<Stack fontSize={FONT.SIZE_14} height="100%" alignItems="center" justifyContent="center">
            No Data Available
        </Stack>)
    }

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                width="100%"
                height="100%"
                data={data}
                margin={{
                    top: 0,
                    right: 0,
                    left: -20,
                    bottom: -8,
                }}
            >
                <CartesianGrid vertical={false} stroke={alpha(colours.black, 0.1)} />
                <XAxis
                    dataKey="name"
                    axisLine={{ stroke: alpha(colours.black, 0.1) }}
                    tickLine={{ strokeWidth: 0 }}
                    fontFamily={"'Poppins', sans-serif"}
                    fontSize={FONT.SIZE_12}
                    tick={{
                        fill: colours.black,
                        fontSize: FONT.SIZE_12,
                        fontFamily: "'Poppins', sans-serif",
                    }}
                />
                <YAxis
                    axisLine={{ stroke: alpha(colours.black, 0.1) }}
                    tickLine={{ strokeWidth: 0 }}
                    domain={[0, 100]}
                    fontFamily={"'Poppins', sans-serif"}
                    fontSize={FONT.SIZE_12}
                    tickFormatter={(d) => {
                        return d != null ? d.toFixed(0) + "%" : "%";
                    }}
                    tick={{
                        fill: colours.black,
                        fontSize: FONT.SIZE_12,
                        fontFamily: "'Poppins', sans-serif",
                    }}
                />
                <Tooltip
                    key="tooltipbar"
                    formatter={(value) => {
                        if (value && typeof value === "number" && typeof value?.toFixed === "function") {
                            return value?.toFixed(0) + "%";
                        }
                        return value ? value + "%" : "%";
                    }}
                    contentStyle={ChartTooltip(colours, FONT)}
                    itemStyle={{ color: colours.white }}
                    cursor={false}
                />
                <defs>
                    {bars.map((bar, index) => (
                        <linearGradient
                            key={"linear-gradient-" + index}
                            id={`colorUv${index}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="100%"
                            spreadMethod="reflect"
                        >
                            <stop offset="0" stopColor={bar.barColors[0]} />
                            <stop offset="1" stopColor={bar.barColors[1]} />
                        </linearGradient>
                    ))}
                </defs>

                {/* Patterns - for color vision mode */}
                <defs>
                    {bars.map((bar, index) => (
                        <AppChartWavePattern patternId="chartWave" color={bar.barColors[0]} index={index} key={`wave-${index}`} />
                    ))}
                </defs>
                <defs>
                    {bars.map((bar, index) => (
                        <AppChartCirclePattern patternId="chartCircle" color={bar.barColors[0]} index={index} key={`circle-${index}`} />
                    ))}
                </defs>

                {bars.map((bar, barIndex) => (<Bar
                    key={bar.dataKey}
                    dataKey={bar.dataKey}
                    stackId="a"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={80}
                >
                    {data.map((entry, index) => {
                        const isNeutral = bar.dataKey.toLowerCase().includes("neutral");
                        const patternId = barIndex === 0 ? "chartWave" : "chartCircle";
                        const patternFill =
                            themeMode === THEME_MODES.COLOR_VISION && !isNeutral
                                ? `url(#${patternId}${barIndex})`
                                : `url(#colorUv${barIndex})`;

                        return <Cell key={`cell-${bar.dataKey}-${index}`} fill={patternFill} />;
                    })}
                </Bar>))}
            </BarChart>
        </ResponsiveContainer>
    )
}

export default AppBarChart